import { TopicHeatmapVMConfig } from 'components/topic/heatmap/heatmap.interfaces';
import CommentsView from 'enums/comments/views';
import { CustomElementConfig } from 'extensions/knockout/bindings/customElement';
import { Observable, PureComputed, Subscribable } from 'knockout';
import device from 'managers/device';
import events from 'managers/events';
import ActivityFormManager from 'managers/topic/form';
import { withEffect } from 'mixins/withEffect';
import { withEffects } from 'mixins/withEffects';
import CommentsFilters from 'models/commentsFilters';
import { Person } from 'models/person';
import { SimpleTopic } from 'models/simpleTopic';
import TaskActivityViewType from '../enums/viewType';
import { TaskActivityViewLinkModel as ViewLink } from '../viewlink';
import {createExtendedContainer} from "../../../../configuration/inversify/container/extended";
import {IWCCStorageManager} from "../../../../managers/iStorage";
import {wccModules} from "../../../../enums/wccModules";
import DiscussionManager from "../../../../managers/discussion";

export default class TaskActivityViewLinksManager {
    private effects = withEffects();
    private storage: IWCCStorageManager
    
    activeView = ko.observable(TaskActivityViewType.all);

    viewLinks: PureComputed<Array<ViewLink>>
    disposeContainer: Action<[]>
    
    constructor(
        activeViewName: Observable<string | undefined>,
        taskId: Subscribable<string | undefined>,
        user: Subscribable<Person | undefined>,
        topic: Subscribable<SimpleTopic | undefined>,
        editorConfigManager: ActivityFormManager,
        commentsFilters: CommentsFilters
    ) {
        const effects = this.effects;

        const [container, disposeContainer] = createExtendedContainer();
        this.storage = container.get<IWCCStorageManager>(wccModules.storage);
        this.disposeContainer = disposeContainer;

        let discussionManager = this.storage.get(DiscussionManager, { discussionId: taskId });
        let discussion = discussionManager.pluck(d => d.discussion);
        
        const observerNotifying: Observable<number | undefined> = ko.observable<number | undefined>(enums.ObserverNotifyingType.Never.value);
        
        effects.register(d => {
            if (d != undefined) {
                observerNotifying(d.observerNotifying());
            }
        }, [discussion]);
        
        const isModerator = user.pluck(u => u.isModerator, false);
        const topicId = topic.pluck(t => t.id);
        const shouldBeAnswered = topic.pluck(t => t.shouldBeAnswered, false);
        const toDoCount = topic.pluck(t => t.toDoCount, 0);

        const formConfig = editorConfigManager.getEditorConfig('form');
        const previewConfig = editorConfigManager.getEditorConfig('preview');

        const heatmapFormConfig = <CustomElementConfig<TopicHeatmapVMConfig>>{
            name: 'topic-heatmap',
            params: {
                model: {
                    topicId: topicId,
                    showAllPins: true,
                    rasterize: true
                }
            }
        }

        const viewLinks = {
            all: new ViewLink({ title: labels.All, type: TaskActivityViewType.all, commentsView: CommentsView.all, mainForm: formConfig, todo: shouldBeAnswered }),
            mine: new ViewLink({ title: labels.Mine, type: TaskActivityViewType.mine, commentsView: CommentsView.mine, mainForm: formConfig }),
            following: new ViewLink({ title: labels.Following, type: TaskActivityViewType.following, commentsView: CommentsView.following, mainForm: formConfig }),
            unread: new ViewLink({ title: labels.Unread, type: TaskActivityViewType.unread, commentsView: CommentsView.unread, todo: toDoCount })
        }

        const moderatorViewLinks = {
            allPosts: new ViewLink({ title: labels.All, type: TaskActivityViewType.all, commentsView: CommentsView.all }),
            mobileAllPosts: new ViewLink({ title: labels.All, type: TaskActivityViewType.all, commentsView: CommentsView.all, mainForm: formConfig, secondaryForm: previewConfig }),
            unreadPosts: new ViewLink({ title: labels.Unread, type: TaskActivityViewType.unread, commentsView: CommentsView.unread, todo: toDoCount }),
            participantsForm: new ViewLink({ title: labels.ViewForm, type: TaskActivityViewType.participantsForm, mainForm: previewConfig }),
            moderatorForm: new ViewLink({ title: labels.AddModPost, type: TaskActivityViewType.moderatorForm, mainForm: formConfig }),
            heatmap: new ViewLink({ title: labels.Heatmap, type: TaskActivityViewType.heatmap, customForm: heatmapFormConfig }),
        }

        this.viewLinks = ko.pureComputed(() => {
            const result = new Array<ViewLink>();
            const currentUser = user();
            const currentTopic = topic();

            if (currentUser != undefined && currentTopic != undefined && editorConfigManager.ready()) {
                if (currentUser.isModerator() || currentUser.isObserver()) {
                    if (device.isMobile())
                        result.push(moderatorViewLinks.mobileAllPosts);
                    else
                        result.push(moderatorViewLinks.allPosts);

                    if (currentUser.isModerator() || (currentUser.isObserver() && observerNotifying() != enums.ObserverNotifyingType.Never.value))
                        result.push(moderatorViewLinks.unreadPosts);

                    if (currentTopic.isHeatMap())
                        result.push(moderatorViewLinks.heatmap);

                    if (!device.isMobile()) {
                        if (previewConfig() != undefined)
                            result.push(moderatorViewLinks.participantsForm);

                        if (!commentsFilters.isActive() && currentUser.isModerator() && !currentTopic.isHomework)
                            result.push(moderatorViewLinks.moderatorForm);
                    }
                } else {
                    result.push(viewLinks.all, viewLinks.mine);

                    //if (!currentTopic.isIdeation())
                    //    result.push(viewLinks.following);

                    result.push(viewLinks.unread);
                }
            }

            return result;
        }).extend({ deferred: true });

        const activeViewLink = this.viewLinks.find(l => l.isActive());

        effects.register(viewLinks => {
            if (viewLinks.length > 0) {
                return [
                    withEffect((activeViewName) => {
                        if (viewLinks.length > 0) {
                            let targetViewLink = activeViewName != undefined ?
                                viewLinks.find(l => TaskActivityViewType[l.type()].toLowerCase() == activeViewName.toLowerCase()) :
                                undefined;

                            if (targetViewLink == undefined)
                                targetViewLink = viewLinks[0];

                            if (targetViewLink != activeViewLink())
                                this.activeView(targetViewLink.type());
                        }
                    }, [activeViewName]),

                    withEffect((activeView) => {
                        const newName = TaskActivityViewType[activeView];

                        if (newName != activeViewName())
                            activeViewName(newName);
                    }, [this.activeView]),

                    withEffect(activeView => {
                        if (activeViewLink()?.type() != activeView)
                            viewLinks.forEach(l => l.isActive(l.type() == activeView));
                    }, [this.activeView]),

                    withEffect(activeViewLink => {
                        if (activeViewLink != undefined && activeViewLink.type() != this.activeView())
                            this.activeView(activeViewLink.type());
                    }, [activeViewLink]),

                    viewLinks.map(link => withEffect(isActive => {
                        if (isActive)
                            viewLinks.forEach(otherLink => {
                                if (otherLink != link)
                                    otherLink.isActive(false);
                            });
                    }, [link.isActive], false))
                ]
            }
        }, [this.viewLinks]);

        effects.register(isModerator => {
            if (isModerator) {
                return events.commentAdded.on(targetTopicId => {
                    if (topicId() == targetTopicId && this.activeView() == TaskActivityViewType.moderatorForm)
                        this.toDefaultView();
                });
            }
        }, [isModerator]);
    }

    toDefaultView() {
        this.activeView(TaskActivityViewType.all);
    }

    dispose() {
        this.effects.dispose();
        this.disposeContainer();
    }
}